import { Link } from "react-router-dom";
import type { ReactNode } from "react";

function ComparisonTable({ headers, rows, caption }: { headers: string[]; rows: string[][]; caption: string }) {
  return <div className="not-prose my-7 overflow-x-auto border-y-2 border-foreground-900 bg-white">
    <table className="w-full min-w-[640px] border-collapse text-left text-sm">
      <caption className="p-3 text-left font-bold">{caption}</caption>
      <thead className="bg-background-100"><tr>{headers.map(header => <th key={header} scope="col" className="p-3">{header}</th>)}</tr></thead>
      <tbody>{rows.map(row => <tr key={row[0]} className="border-t border-background-300">{row.map((cell, index) => index === 0 ? <th key={index} scope="row" className="p-3 font-bold">{cell}</th> : <td key={index} className="p-3 leading-6">{cell}</td>)}</tr>)}</tbody>
    </table>
  </div>;
}

function SourceLink({ href, children }: { href: string; children: ReactNode }) {
  return <a href={href} target="_blank" rel="noreferrer">{children} ↗</a>;
}

export default function Iphone18ProMnpArticle() {
  return <>
    <section id="answer">
      <p className="text-[1.08em] leading-[1.9] text-foreground-900">iPhone 18 Proが欲しい。でも、22万円となるとためらう。同じiPhoneでも、購入先や買い方によって支払う金額は数万円変わります。</p>
      <p>購入先を決める前に、まず考えたいのはひとつです。<strong>約2年で返却して買い替えるか、返却せずに使い続けるか。</strong>この違いで、候補となる購入先が変わります。</p>
      <h2>先に結論：返却するかどうかで選ぶ</h2>
      <ComparisonTable caption="この記事の条件で端末代を比べた場合" headers={["使い方", "候補となる購入先", "支払額の目安"]} rows={[
        ["約2年で返却して買い替える", "au・SoftBank", "端末代＋利用料で約4.5万円。2社の差は860円"],
        ["返却せずに使い続ける", "Appleで端末のみ購入", "端末代219,800円。auの完済額より5,000円安い"],
      ]} />
      <p>以下は、<strong>iPhone 18 Pro 256GBをオンラインで購入する場合</strong>の比較です。</p>
      <ul>
        <li>通信会社では、今の電話番号のまま他社から乗り換える（MNP）場合の割引を反映しています。機種変更や新しい電話番号での契約は価格が異なります。</li>
        <li>Appleは端末のみ購入する場合です。回線は別に選びます。</li>
        <li>全社ともポイント還元・下取りは差し引かず、通信費・契約事務手数料・補償料は含めていません。</li>
      </ul>
      <p><a href="#return-price">約2年で返却する場合を見る</a> ／ <a href="#price">返却せずに使い続ける場合を見る</a></p>
      <aside>
        <strong>Appleで買うなら、回線は別に選べます。</strong>
        <p>iPhoneをAppleで購入し、通信契約だけを乗り換える方法です。通信会社で端末を同時購入する場合の値引きとは条件が違うため、端末代とSIMのみの乗り換え特典を分けて比較します。</p>
        <p><Link to="/articles/sim-only-mnp-campaigns">SIMのみMNP特典を5社で比べる →</Link></p>
      </aside>
    </section>

    <section id="return-price">
      <h2>約2年で返却する人</h2>
      <h3>仕組みは3ステップ</h3>
      <ol>
        <li>返却プログラムを利用して、iPhoneを分割払いで購入する。</li>
        <li>約2年使ったら、指定された時期に購入先へ返却する。</li>
        <li>端末の状態などの条件を満たすと、残りの分割代金の支払いが不要になる。</li>
      </ol>
      <p>返却したiPhoneは手元に残りません。その代わり、本体代を全額払うよりも、今回の端末代を抑えられます。</p>
      <h3>返却までの端末代と、返却時の利用料を比較</h3>
      <ComparisonTable caption="MNPで購入し、約2年で返却する場合（税込・通信費別）" headers={["購入先", "①端末代（分割払いの合計）", "②返却時の利用料・手数料", "①＋② 合計", "返却時期"]} rows={[
        ["au", "22,900円", "22,000円", "44,900円", "25か月目まで"],
        ["SoftBank", "23,760円", "22,000円", "45,760円", "25か月目に返却申込"],
        ["ドコモ／ahamo", "44,814円", "22,000円", "66,814円", "23か月目"],
        ["楽天モバイル", "127,944円", "3,300円", "131,244円", "25か月目"],
      ]} />
      <ul>
        <li><strong>①端末代：</strong>ドコモ／ahamoは22,000円、auは44,000円、SoftBankは33,000円のMNP割引を反映済みです。楽天モバイルは通常48回払いで計算しています。</li>
        <li><strong>②利用料・手数料：</strong>端末代とは別に、返却の際にかかる費用です。合計欄は利用料が免除されない場合です。</li>
        <li><strong>返却時期：</strong>各社の制度に合わせた23〜25か月目の比較です。同じ日に返却する想定ではありません。</li>
      </ul>
      <p>全社とも通信費とポイント還元は含めていません。期限どおりに返却し、端末の状態も各社の基準を満たす場合の金額です。</p>
      <h3>次も同じ会社で購入すると、利用料が免除される場合があります</h3>
      <p>ドコモ／ahamo・au・SoftBankでは、次も指定の購入先で対象スマホを購入するなどの条件を満たすと、②の利用料22,000円がかからなくなります。その場合、今回のiPhoneに支払う金額は次のとおりです。</p>
      <ul><li>au：22,900円</li><li>SoftBank：23,760円</li><li>ドコモ／ahamo：44,814円</li></ul>
      <p>次に購入するスマホの代金は含みません。<strong>2年後の購入先を決めていない人は、利用料を含む合計額（auなら44,900円）を予算にしてください。</strong></p>
      <p>広告の「実質22,900円」は、MNP割引や返却、次回購入による利用料免除などの条件を満たした場合です。22,900円を払えば、返却せずに使い続けられるという意味ではありません。</p>
      <h3>auとSoftBankで迷ったら、決め手は通信費</h3>
      <p>利用料を含めた2社の差は860円です。24か月で割ると、月あたり約36円。通信費が月に数百円違えば、この差を上回ります。</p>
      <p><strong>必要なデータ量や通話条件、サポートが同じように満たせるなら、通信費が安いほうが有力です。</strong>端末のMNP割引が使える料金プランかどうかも、合わせて確認します。</p>
      <p>ドコモ／ahamoはauより21,914円高くなります。ただし、通信費が月914円以上安ければ、24か月分でこの差を上回ります。これは通信費の差を仮定した計算で、各社の実際の料金差ではありません。</p>
      <h3>申込み前に確認すること</h3>
      <ul>
        <li><strong>au：</strong>44,000円のMNP割引は対象プランへの加入が条件です。「スマホミニプラン＋」とUQ mobile・povo1.0からの変更は対象外。返却は13〜25か月目の回収などの条件があります。</li>
        <li><strong>SoftBank：</strong>オンラインでMNPし、48回払いで購入する場合です。一括・24回払いでは割引額が変わります。25か月目に申し込み、翌月末までに回収・査定を完了する必要があります。</li>
        <li><strong>ドコモ／ahamo：</strong>MNP割引が使える場合の価格です。23か月目の返却が目安です。ドコモからahamoへのプラン変更はMNPではありません。</li>
        <li><strong>楽天モバイル：</strong>楽天カードでの48回払いが条件です。</li>
        <li><strong>全社共通：</strong>画面割れなどで査定基準を満たさない場合、追加費用がかかったり、返却特典を利用できなかったりすることがあります。</li>
      </ul>
      <p><Link to="/articles/return-program">4社の返却時期と追加費用を確認する →</Link></p>
    </section>

    <section id="price">
      <h2>返却せずに使い続ける人</h2>
      <h3>本体代を払い終わるまでの総額を比較</h3>
      <ComparisonTable caption="返却せず完済する場合（税込・通信費別）" headers={["購入先", "割引前の本体価格", "MNP割引", "端末代の支払総額"]} rows={[
        ["Apple（端末のみ）", "219,800円", "なし", "219,800円"],
        ["au", "268,800円", "44,000円", "224,800円"],
        ["SoftBank", "268,560円", "33,000円", "235,560円"],
        ["ドコモ／ahamo", "263,230円", "22,000円", "241,230円"],
        ["楽天モバイル", "255,900円", "計上なし", "255,900円"],
      ]} />
      <ul>
        <li>返却しないため、返却時の利用料・手数料はかかりません。</li>
        <li>通信会社の支払総額はMNP割引後です。対象プランなど、割引の条件を満たす場合で計算しています。</li>
        <li>SoftBankの235,560円は、48回払いで割引を受けて完済する場合です。一括払いの価格ではありません。</li>
        <li>全社ともポイント還元・下取り・通信費は含めていません。</li>
      </ul>
      <h3>この比較条件ではAppleが安く、回線も別に選べます</h3>
      <p>端末代だけを比べるとAppleの219,800円が最も安く、auの完済額より5,000円安くなります。Appleで端末のみ購入すれば、端末割引のために特定の料金プランへ加入する必要がありません。</p>
      <p>回線だけ乗り換える場合には、ポイント特典が使えることもあります。たとえばahamoには、端末を同時購入せず他社から乗り換えると、条件を満たした場合に20,000ポイントを受け取れるキャンペーンがあります。</p>
      <p>ahamoでiPhoneも同時購入する場合は対象外です。Appleで端末だけを別に購入し、ahamoではSIM・eSIMのみをMNP契約する場合が候補になります。事前エントリーや開通期限などの条件があり、ポイントは現金値引きとは異なるため、この表の端末代からは差し引いていません。<SourceLink href="https://ahamo.com/special/lp/dpointcp/001.html">ahamoのポイント条件を確認する</SourceLink>。</p>
      <p><Link to="/articles/sim-only-mnp-campaigns">Appleで端末を買う人向け：SIMのみMNP特典を5社で比較する →</Link></p>
      <h3>払い終わった後も、そのまま使い続けられます</h3>
      <p>売却や下取りを前提にする必要はありません。必要に応じてバッテリー交換や修理をしながら、使い続ける選択もできます。長く使うほど、端末代の1年あたりの負担は小さくなります。</p>
    </section>

    <section id="choice">
      <h2>どちらにするか迷ったら</h2>
      <ComparisonTable caption="使い方に合う買い方を選ぶ" headers={["使い方・気になること", "選び方"]} rows={[
        ["2年ごとに新しいiPhoneにしたい", "返却する買い方。端末代＋利用料が近いau・SoftBankから、通信費も含めて比較"],
        ["4年以上使いたい", "返却しない買い方。Appleで端末のみ購入し、回線を別に選ぶ方法が候補"],
        ["傷や故障で返却時の負担が増えるのが心配", "返却しない買い方なら返却査定の心配は不要。ただし修理費は別にかかる"],
      ]} />
    </section>

    <section id="compare">
      <h2>買い方が決まったら、通信費も合わせて確認</h2>
      <p>約2年で返却するなら、まずauとSoftBankの端末代と利用料が近いことを押さえます。返却せず長く使うなら、Appleの端末代を基準にできます。</p>
      <p>そこに毎月の通信費を合わせて、購入先を選びます。通信費を比べるときは、次の条件をそろえてください。</p>
      <ol><li>毎月使うデータ量と、通話時間。</li><li>家族割や自宅の光回線とのセット割が使えるか。</li><li>端末のMNP割引に必要な料金プランに加入できるか。</li></ol>
      <p>この記事の通信費24か月分という計算は比較の目安です。実際の返却時期は23〜25か月目なので、返却までの総額は実際の利用月数で確認します。契約事務手数料や必要な補償料も別に加えます。</p>
      <p><Link to="/articles/which-plan-is-cheapest">自分のデータ使用量で通信費を比較する →</Link></p>
    </section>

    <section id="official">
      <h2>公式ページで最終確認</h2>
      <ul>
        <li><SourceLink href="https://www.apple.com/jp/shop/buy-iphone/iphone-18-pro">Apple：iPhone 18 Pro</SourceLink></li>
        <li><SourceLink href="https://www.docomo.ne.jp/binary/pdf/info/notice/page/260911_00.pdf">ドコモ：価格表（PDF）</SourceLink></li>
        <li><SourceLink href="https://ahamo.com/products/iphone/iphone-18-pro/index.html">ahamo：iPhone 18 Pro</SourceLink></li>
        <li><SourceLink href="https://onlineshop.au.com/info/CSfViewInfo.jsp?no=257&sort=6">au：MNP割引と返却の条件</SourceLink></li>
        <li><SourceLink href="https://www.softbank.jp/mobile/set/common/pdf/products/apple-autumn-2026/model-price-iphone.pdf">SoftBank：価格表（PDF）</SourceLink></li>
        <li><SourceLink href="https://network.mobile.rakuten.co.jp/product/iphone/iphone-18-pro/">楽天モバイル：iPhone 18 Pro</SourceLink></li>
      </ul>
      <p>公式情報確認日：2026年9月23日。価格・割引・在庫・対象条件は変わるため、申込み画面で最終確認してください。</p>
    </section>
  </>;
}
